import React, { useState, useContext, useEffect, useRef } from 'react';
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, Search, User, LogOut, ShieldAlert, MapPin, Package, Loader2 } from 'lucide-react';
import { useCart } from '../../hooks/useCart';
import { useWishlistContext } from '../../context/WishlistContext';
import { useToast } from '../../context/ToastContext';
import { AuthContext } from '../../context/AuthContext';
import TopBanner from '../../components/layout/TopBanner';
import { logo } from "../../assets/images";
import { findProductsByName } from '../../api/productApi/ProductApi';


export default function Header({
                                 onOpenCart,
                                 onSelectProduct,
                                 setIsAuthOpen,
                                 setIsLoginMode
                               }) {
  const { cart } = useCart();
  const { wishlist, openWishlist } = useWishlistContext();


  // State quản lý tìm kiếm trực tiếp
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  const { showToast } = useToast();
  const navigate = useNavigate();

  const { user, logout } = useContext(AuthContext);
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Hàm bóc tách URL ảnh an toàn từ object product
  const getProductImage = (product) => {
    if (!product) return null;

    // 1. Nếu có field image/imageUrl chuẩn dạng chuỗi
    let imgPath = product.imageUrl || product.image || product.primaryImage || product.mainImage || product.thumbnail;

    // 2. Nếu images là mảng (Array)
    if (!imgPath && Array.isArray(product.images) && product.images.length > 0) {
      const firstImg = product.images[0];
      imgPath = typeof firstImg === 'string' ? firstImg : (firstImg?.imageUrl || firstImg?.url || firstImg?.path);
    }

    if (!imgPath) return null;

    // 3. Xử lý đường dẫn nếu từ backend trả về dạng tương đối (ví dụ: /uploads/abc.png)
    if (typeof imgPath === 'string' && imgPath.startsWith('/')) {
      return `https://japanichiba.duckdns.org${imgPath}`;
    }

    return imgPath;
  };

  // Đóng dropdown khi click ra ngoài thanh tìm kiếm
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Xử lý gõ tìm kiếm động
  const handleInputChange = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (!value.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowDropdown(true);

    try {
      const res = await findProductsByName(value.trim());
      const productsList = res?.data || res || [];
      setSearchResults(Array.isArray(productsList) ? productsList : []);
    } catch (error) {
      console.error("Lỗi khi tìm kiếm sản phẩm:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectProductItem = (product) => {
    // Lưu sản phẩm vừa chọn vào bộ nhớ tạm để trang Home bốc sang
    localStorage.setItem('quickViewTarget', JSON.stringify(product));

    // Phát ra một tín hiệu (Event) báo cho trang Home biết là người dùng vừa click tìm kiếm
    window.dispatchEvent(new Event('triggerQuickViewFromSearch'));

    setShowDropdown(false);
    setSearchTerm('');
  };

  const handleLogout = async () => {
    try {
      await axios.post("https://japanichiba.duckdns.org/api/v1/auth/logout");
    } catch (error) {
      console.error("Lỗi đăng xuất từ server:", error);
    } finally {
      logout();
      showToast("Đăng xuất thành công!", "success");
    }
  };

  const displayName = user?.fullName || user?.name || (user?.email ? user.email.split('@')[0] : "User");
  const isAdmin = String(user?.role || "").toUpperCase() === "ADMIN" || Number(user?.id) === 1 || (user?.email && user.email.toLowerCase().includes("admin"));

  return (
      <>
        <TopBanner />
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-100 transition-all shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-6">

            {/* 1. Logo */}
            <div className="flex items-center space-x-2 cursor-pointer shrink-0">
              <Link to="/" className="flex items-center space-x-2">
                <img src={logo} alt="JapanICHIBA Logo" className="h-10 w-auto object-contain" />
                <span className="text-2xl font-bold text-slate-900 font-serif tracking-tight">
                Japan<span className="text-indigo-600">ICHIBA</span>
              </span>
              </Link>
            </div>

            {/* 2. Thanh Tìm Kiếm Trực Tiếp */}
            <div className="flex-1 max-w-2xl relative" ref={searchRef}>
              <div className="relative flex items-center">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => searchTerm.trim() && setShowDropdown(true)}
                    placeholder="Tìm kiếm sản phẩm, thương hiệu, danh mục..."
                    className="w-full bg-slate-100/80 border border-transparent focus:border-indigo-500 focus:bg-white text-slate-800 text-xs font-medium rounded-2xl pl-11 pr-10 py-3 transition duration-200 outline-none shadow-inner"
                />
                <Search className="w-5 h-5 text-slate-400 absolute left-3.5 pointer-events-none" />
                {isSearching && (
                    <Loader2 className="w-4 h-4 text-indigo-600 animate-spin absolute right-3.5" />
                )}
              </div>

              {/* Dropdown Gợi Ý Kết Quả Tìm Kiếm */}
              {showDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50 max-h-96 overflow-y-auto animate-fadeIn">
                    {isSearching ? (
                        <div className="p-4 text-center text-xs text-slate-500">Đang tìm kiếm...</div>
                    ) : searchResults.length > 0 ? (
                        <div className="py-2">
                          <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            Kết quả tìm kiếm ({searchResults.length})
                          </div>
                          {searchResults.map((product) => {
                            const imageSrc = getProductImage(product);
                            return (
                                <div
                                    key={product.id || product.productId}
                                    onClick={() => handleSelectProductItem(product)}
                                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer transition"
                                >
                                  {imageSrc ? (
                                      <img
                                          src={imageSrc}
                                          alt={product.name || product.title}
                                          className="w-10 h-10 object-cover rounded-lg border border-slate-100 shrink-0"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.style.display = 'none';
                                          }}
                                      />
                                  ) : (
                                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                                        <Package className="w-5 h-5 text-slate-400" />
                                      </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-slate-800 truncate">{product.name || product.title}</p>
                                    <p className="text-xs font-bold text-indigo-600 mt-0.5">
                                      {Number(product.price || 0).toLocaleString('vi-VN')} đ
                                    </p>
                                  </div>
                                </div>
                            );
                          })}
                        </div>
                    ) : (
                        <div className="p-4 text-center text-xs text-slate-500">
                          Không tìm thấy sản phẩm nào khớp với "{searchTerm}"
                        </div>
                    )}
                  </div>
              )}
            </div>

            {/* 3. Action Icons & Auth */}
            <div className="flex items-center space-x-2 sm:space-x-3 shrink-0">

              {/* Wishlist */}
              <button
                  onClick={openWishlist}
                  className="p-2.5 text-slate-600 hover:text-rose-500 hover:bg-slate-50 rounded-2xl transition relative cursor-pointer"
                  title="Yêu thích"
              >
                <Heart className="w-5 h-5" />
                {wishlist.length > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-rose-500 text-white text-[10px] font-extrabold rounded-full flex items-center justify-center">
                  {wishlist.length}
                </span>
                )}
              </button>

              {/* Cart Icon */}
              <button
                  onClick={onOpenCart}
                  className="p-2.5 text-slate-600 hover:text-indigo-600 hover:bg-slate-50 rounded-2xl transition relative cursor-pointer"
                  title="Giỏ hàng"
              >
                <ShoppingBag className="w-5 h-5" />
                {totalItems > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-indigo-600 text-white text-[10px] font-extrabold rounded-full flex items-center justify-center animate-pulse">
                  {totalItems}
                </span>
                )}
              </button>

              {/* Account Profile */}
              {user ? (
                  <div className="relative group cursor-pointer py-2">
                    <div className="flex items-center space-x-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-full hover:bg-slate-100 transition">
                      <div className="w-7 h-7 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-bold text-xs">
                        {displayName ? displayName.charAt(0).toUpperCase() : <User className="w-4 h-4" />}
                      </div>
                      <span className="text-xs font-bold text-slate-700 max-w-[90px] truncate hidden sm:inline-block">
                    {displayName}
                  </span>
                    </div>

                    <div className="absolute right-0 top-full w-56 bg-white border border-slate-200 rounded-2xl shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="px-4 py-2.5 border-b border-slate-100">
                        <p className="text-xs font-bold text-slate-900 truncate">{displayName}</p>
                        <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
                      </div>

                      <div className="py-1">
                        <Link to="/profile" className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition">
                          <User className="w-4 h-4 text-slate-400" /> Thông tin tài khoản
                        </Link>
                        <Link to="/orders" className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition">
                          <Package className="w-4 h-4 text-slate-400" /> Đơn hàng của tôi
                        </Link>
                        <Link to="/addresses" className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition">
                          <MapPin className="w-4 h-4 text-slate-400" /> Sổ địa chỉ giao hàng
                        </Link>

                        {isAdmin && (
                            <Link to="/admin" className="flex items-center gap-2.5 px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 transition border-t border-slate-100 mt-1 pt-2">
                              <ShieldAlert className="w-4 h-4" /> Trang Quản Trị (Admin)
                            </Link>
                        )}
                      </div>

                      <div className="border-t border-slate-100 pt-1 mt-1">
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 transition text-left cursor-pointer"
                        >
                          <LogOut className="w-4 h-4" /> Đăng xuất
                        </button>
                      </div>
                    </div>
                  </div>
              ) : (
                  <div className="flex items-center space-x-2">
                    <button
                        onClick={() => { setIsLoginMode(true); setIsAuthOpen(true); }}
                        className="text-xs font-bold text-slate-700 hover:text-indigo-600 px-2.5 py-2 transition cursor-pointer"
                    >
                      Đăng Nhập
                    </button>
                    <button
                        onClick={() => { setIsLoginMode(false); setIsAuthOpen(true); }}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-3.5 py-2 rounded-full text-xs shadow-md transition cursor-pointer"
                    >
                      Đăng Ký
                    </button>
                  </div>
              )}

            </div>

          </div>
        </header>
      </>
  );
}